import type { ComponentPropsWithoutRef, PropsWithChildren } from 'react';
import classNames from 'classnames';
import { ButtonRounded, Link } from 'is24-corecss';
import { IconS24Document24, IconS24Pen24, IconS24Telephone24 } from '@is24/cosma-ui-icons';
import { useTranslation } from 'react-i18next';
import { createTelLinkHandler } from '@/utils/telLinkUtils';
import adacPartnerLogo from '@/components/content/welcomeModal/adacUpsell/images/adac-partner-logo.webp';
import type { AdacDocument, AdacPolicyDetails } from './model';
import { styles } from './AdacPolicyOverviewModal.styles';
import { adacContactInformation } from '../contactInformation';

export interface PolicyDetailsProps {
  policy: AdacPolicyDetails;
  onEdit?: () => void;
  onOpenDocument?: (document: AdacDocument) => void;
}

const documents: AdacDocument[] = [
  'confirmation',
  'conditions',
  'privacyNotice',
  'insuredPersonInformation',
  'productInformation',
];

type DivClassNameProps = Pick<ComponentPropsWithoutRef<'div'>, 'className'>;

export const PolicyDetails = ({ policy, onEdit, onOpenDocument }: PolicyDetailsProps) => {
  const { t } = useTranslation('adac', { keyPrefix: 'policyOverview' });

  return (
    <div className={`${styles.details} border padding-l palm-padding-xl`}>
      <dl className='margin-none font-body-medium-regular'>
        <Detail label={t('customerNumber')} className='margin-bottom-l'>
          {policy.customerNumber}
        </Detail>
        <Detail label={t('confirmationNumber')} className='margin-bottom-l'>
          {policy.confirmationNumber}
        </Detail>
        <PolicyholderDetails policyholder={policy.policyholder} onEdit={onEdit} className='margin-bottom-l' />
        <AdacEmergencyContact className='margin-bottom-l' />
        <PolicyDocuments onOpenDocument={onOpenDocument} />
      </dl>
    </div>
  );
};

const PolicyholderDetails = ({
  policyholder,
  onEdit,
  className,
}: { policyholder: AdacPolicyDetails['policyholder'] } & Pick<PolicyDetailsProps, 'onEdit'> & DivClassNameProps) => {
  const { t } = useTranslation('adac', { keyPrefix: 'policyOverview' });

  return (
    <Detail label={t('personalDetails')} className={className}>
      <p className='margin-none'>{policyholder.name}</p>
      <p className='margin-none'>{t('dateOfBirth', { date: policyholder.dateOfBirth })}</p>
      <p className='margin-none'>{policyholder.street}</p>
      <p className='margin-bottom-s'>
        {policyholder.postalCode} {policyholder.city}
      </p>
      <ButtonRounded
        variant='outlineStrong'
        size='small'
        startElement={<IconS24Pen24 aria-hidden='true' />}
        aria-label={t('editLabel')}
        onClick={onEdit}
      >
        {t('edit')}
      </ButtonRounded>
    </Detail>
  );
};

const AdacEmergencyContact = ({ className }: DivClassNameProps) => {
  const { t } = useTranslation('adac', { keyPrefix: 'policyOverview' });
  const { phoneNumber, dialNumber } = adacContactInformation;
  const callAdac = createTelLinkHandler(dialNumber);

  return (
    <Detail label={t('emergencyContact')} className={className}>
      <img
        className={`${styles.logo} display-block margin-bottom-l`}
        src={adacPartnerLogo.src}
        srcSet={adacPartnerLogo.srcSet}
        sizes='140px'
        width={1182}
        height={284}
        alt={adacContactInformation.name}
      />
      <p className='margin-bottom-l'>
        {adacContactInformation.street}
        <br />
        {adacContactInformation.postalCodeAndCity}
      </p>
      <Link
        href={`tel:${dialNumber}`}
        variant='weak'
        startElement={<IconS24Telephone24 aria-hidden='true' />}
        onClick={callAdac}
      >
        <span className='font-body-large-bold'>{phoneNumber}</span>
      </Link>
      <p className={`${styles.muted} margin-bottom-none margin-top-xs`}>{t('emergencyAvailability')}</p>
      <p className={`${styles.muted} margin-bottom-none margin-top-xs`}>{t('customerService')}</p>
    </Detail>
  );
};

const PolicyDocuments = ({
  onOpenDocument,
  className,
}: Pick<PolicyDetailsProps, 'onOpenDocument'> & DivClassNameProps) => {
  const { t } = useTranslation('adac', { keyPrefix: 'policyOverview' });

  return (
    <Detail label={t('documents')} className={className}>
      <ul className='margin-none list-spacing-s'>
        {documents.map((document) => {
          const openPolicyDocument = () => onOpenDocument?.(document);
          return (
            <li key={document}>
              <Link
                as='button'
                type='button'
                className='align-left grid-align-start'
                variant='weak'
                startElement={<IconS24Document24 aria-hidden='true' />}
                onClick={openPolicyDocument}
              >
                {t(`document.${document}`)}
              </Link>
            </li>
          );
        })}
      </ul>
    </Detail>
  );
};

const Detail = ({ label, children, className }: PropsWithChildren<{ label: string } & DivClassNameProps>) => (
  <div className={classNames('grid grid-flex gutter-horizontal-l', className)}>
    <dt className='grid-item two-fifths palm-one-whole font-body-medium-bold palm-margin-bottom-m'>{label}</dt>
    <dd className='grid-item three-fifths palm-one-whole margin-left-none'>{children}</dd>
  </div>
);
